import type {
  ICreateModelDto,
  IModel,
  IModelSettingsPayload,
  IModelSettingsResponse,
  IToggleModelEnabledProps,
  IToggleModelEnabledResponse,
  IUpdateModelParams,
  IVerifyApiKeyDto,
  IVerifyApiKeyResponse,
} from "../types/models";
import type { ApiClient } from "./client";

export class ModelsApi {
  constructor(private apiClient: ApiClient) {}

  async getAll(): Promise<IModel[]> {
    const response = await this.apiClient.get<IModel[]>("/models");
    return response.data;
  }

  async getCustomModels(): Promise<IModel[]> {
    const response = await this.apiClient.get<IModel[]>("/models/custom");
    return response.data;
  }

  async getByUuid(uuid: string): Promise<IModel> {
    const response = await this.apiClient.get<IModel>(`/models/${uuid}`);
    return response.data;
  }

  async create(data: ICreateModelDto): Promise<IModel> {
    const response = await this.apiClient.post<IModel>("/models", data);
    return response.data;
  }

  async delete(uuid: string): Promise<void> {
    await this.apiClient.delete(`/models/${uuid}`);
  }

  // NOTE: NSAI-4359 — PATCH /models/:uuid (partial update of a project model).
  // The server returns a { message } acknowledgement, not the model.
  async update({
    uuid,
    data,
  }: IUpdateModelParams): Promise<{ message: string }> {
    const response = await this.apiClient.patch<{ message: string }>(
      `/models/${uuid}`,
      data,
    );
    return response.data;
  }

  async toggleEnabled({
    uuid,
    enabled,
  }: IToggleModelEnabledProps): Promise<IToggleModelEnabledResponse> {
    const response = await this.apiClient.post<IToggleModelEnabledResponse>(
      `/models/${uuid}/toggle-enabled`,
      { enabled },
    );
    return response.data;
  }

  // NOTE: NSAI-4838 — per-(user, project) chat model defaults. The project comes
  // from the request's x-project-uuid header (added by the api client), so the
  // path carries no project id.
  async getModelSettings(): Promise<IModelSettingsResponse> {
    const response =
      await this.apiClient.get<IModelSettingsResponse>("/models/settings");
    return response.data;
  }

  async updateModelSettings(
    data: Partial<IModelSettingsPayload>,
  ): Promise<IModelSettingsResponse> {
    const response = await this.apiClient.patch<IModelSettingsResponse>(
      "/models/settings",
      data,
    );
    return response.data;
  }

  async verifyApiKey(data: IVerifyApiKeyDto): Promise<IVerifyApiKeyResponse> {
    const response = await this.apiClient.post<IVerifyApiKeyResponse>(
      "/models/verify-api-key",
      data,
    );
    return response.data;
  }
}
